import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GB, s_GC, s_TI } from '../../shared/styles';
import Avatar from '../../components/ui/Avatar';
import StatusDot from '../../components/ui/StatusDot';
import { fetchUsers } from '../../shared/dbService';
import ConnectionStrength from '../social/ConnectionStrength';

function FriendsScreen({lang,currentUserId,friends,friendRequests,allUsers,onFriendRequest,onAcceptFriend,onDeclineFriend,onOpenProfile,onOpenChat}) {
  const nl=lang==="nl";
  const [subtab,setSubtab]=useState("friends");
  const [discoverSearch,setDiscoverSearch]=useState("");
  const [dbUsers,setDbUsers]=useState([]);
  const [loading,setLoading]=useState(true);
  const hasLoadedOnce=useRef(false);

  useEffect(()=>{
    if(!hasLoadedOnce.current) setLoading(true);
    fetchUsers(currentUserId)
      .then(users=>{ if(Array.isArray(users)) setDbUsers(users); })
      .catch(()=>{})
      .finally(()=>{ setLoading(false); hasLoadedOnce.current=true; });
  },[currentUserId]);

  const allKnownUsers = [...dbUsers, ...(allUsers||[])].filter((u,i,arr)=>arr.findIndex(x=>x.id===u.id)===i);

  const incoming  = (friendRequests||[]).filter(r=>r.to===currentUserId&&r.status==="pending");
  const outgoing  = (friendRequests||[]).filter(r=>r.from===currentUserId&&r.status==="pending");
  const friendUsers = allKnownUsers.filter(u=>friends.includes(u.id));
  const otherUsers  = allKnownUsers.filter(u=>
    u.id!==currentUserId &&
    !friends.includes(u.id) &&
    !outgoing.some(r=>r.to===u.id)
  );

  const requestCount = incoming.length + outgoing.length;

  if (loading) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"60px 0",gap:14}}>
      <style>{`@keyframes sl-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{width:36,height:36,borderRadius:"50%",border:`3px solid ${_T.cardBorderBot}`,borderTopColor:_T.accent,animation:"sl-spin 0.8s linear infinite"}}/>
      <div style={{color:"#8899BB",fontSize:13}}>{nl?"Laden...":"Loading..."}</div>
    </div>
  );

  return(
    <div>
      <div style={{display:"flex",background:_T.bg4,borderRadius:14,padding:4,marginBottom:14,gap:2}}>
        {[
          {id:"friends", label:nl?"Vrienden":"Friends", count:friendUsers.length},
          {id:"requests",label:nl?"Verzoeken":"Requests", count:requestCount},
          {id:"discover",label:nl?"Ontdekken":"Discover", count:0},
        ].map(t=>(
          <button key={t.id} onClick={()=>setSubtab(t.id)} style={{flex:1,background:subtab===t.id?`linear-gradient(135deg,${_T.grad1},${_T.grad2})`:"transparent",border:"none",borderRadius:10,padding:"9px 4px",cursor:"pointer"}}>
            <span style={{fontSize:9,color:subtab===t.id?"#0a1220":C.silver,fontWeight:subtab===t.id?700:400,whiteSpace:"nowrap"}}>
              {t.label}{t.count>0?` (${t.count})`:""}
            </span>
          </button>
        ))}
      </div>

      {/* ── Friends tab ── */}
      {subtab==="friends"&&(
        <div>
          {friendUsers.length===0&&(
            <div style={{...s_GC({textAlign:"center",padding:28}),color:C.silver,fontSize:13}}>
              {nl?"Nog geen vrienden.":"No friends yet."}
            </div>
          )}
          {friendUsers.map(u=>(
            <div key={u.id} style={s_GC({padding:14,marginBottom:8})}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{position:"relative"}}>
                  <Avatar user={u} size={46} profileImage={u.picture||null} onClick={()=>onOpenProfile(u)}/>
                  <span style={{position:"absolute",bottom:1,right:1}}><StatusDot status={u.status||"offline"} size={11}/></span>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                    <div style={{color:C.white,fontSize:14,fontWeight:600,cursor:"pointer"}} onClick={()=>onOpenProfile(u)}>
                      {u.name}
                    </div>
                    {u.isResting && (
                      <span style={{fontSize:9,color:"#2A9E82",background:"#2A9E8218",border:"1px solid #2A9E8244",borderRadius:20,padding:"1px 7px",fontWeight:700,flexShrink:0}}>
                        🌙 {nl?"Rust":"Resting"}
                      </span>
                    )}
                    {u.lastMood && !u.isResting && (() => {
                      const MD = {great:"😄",good:"🙂",neutral:"😐",low:"😔",bad:"😞"};
                      return <span style={{fontSize:12}}>{MD[u.lastMood]||""}</span>;
                    })()}
                  </div>
                  {/* Connection strength dots */}
                  <ConnectionStrength
                    currentUserId={currentUserId}
                    targetUserId={u.id}
                    lang={lang}
                    mode="dots"
                    showLabel={true}
                  />
                </div>
                <div style={{display:"flex",gap:6}}>
                  {!u.isResting&&u.status!=="resting"&&<button onClick={()=>onOpenChat(u)} style={s_GB({padding:"6px 12px",fontSize:11,borderRadius:8})}>💬</button>}
                  <button onClick={()=>onOpenProfile(u)} style={{background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,color:C.silver,borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:11}}>👤</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Requests tab ── */}
      {subtab==="requests"&&(
        <div>
          {incoming.length>0&&(
            <div style={{fontSize:11,color:_T.accent,letterSpacing:2,fontWeight:600,marginBottom:8,marginTop:4}}>{nl?"ONTVANGEN":"RECEIVED"}</div>
          )}
          {incoming.map(r=>{
            const u=allKnownUsers.find(x=>x.id===r.from);
            if(!u)return null;
            return(
              <div key={r.from} style={s_GC({padding:14,marginBottom:8,display:"flex",alignItems:"center",gap:12})}>
                <Avatar user={u} size={44} profileImage={u.picture||null} onClick={()=>onOpenProfile(u)}/>
                <div style={{flex:1}}>
                  <div style={{color:C.white,fontSize:14,fontWeight:600,cursor:"pointer"}} onClick={()=>onOpenProfile(u)}>{u.name}</div>
                  <div style={{fontSize:11,color:C.silver,marginTop:2}}>{nl?"Wil je vriend zijn":"Wants to be your friend"}</div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>onAcceptFriend(u.id)} style={s_PB({padding:"7px 14px",fontSize:12,borderRadius:9})}>✓</button>
                  <button onClick={()=>onDeclineFriend(u.id)} style={{background:"transparent",border:`1px solid ${C.red}44`,color:C.red,borderRadius:9,padding:"7px 12px",cursor:"pointer",fontSize:12}}>✕</button>
                </div>
              </div>
            );
          })}

          {outgoing.length>0&&(
            <div style={{fontSize:11,color:C.silver,letterSpacing:2,fontWeight:600,marginBottom:8,marginTop:incoming.length>0?14:4}}>{nl?"VERZONDEN":"SENT"}</div>
          )}
          {outgoing.map(r=>{
            const u=allKnownUsers.find(x=>x.id===r.to);
            const name=u?.name||r.to;
            const inviteUrl=r.invite_token?`https://senselifting.com/?friendinvite=${r.invite_token}`:null;
            const myName=allKnownUsers.find(x=>x.id===currentUserId)?.name||'Someone';
            const waMsg=nl
              ?`Hoi! Ik heb je een vriendschapsverzoek gestuurd op Sense Lifting. Accepteer het hier: ${inviteUrl}`
              :`Hi! I sent you a friend request on Sense Lifting. Accept it here: ${inviteUrl}`;
            const emailSubject=nl?'Vriendschapsverzoek op Sense Lifting':'Friend request on Sense Lifting';
            const emailBody=nl
              ?`Hoi,\n\n${myName} wil je toevoegen als vriend op Sense Lifting.\n\nKlik hier om te accepteren of te weigeren:\n${inviteUrl}\n\nGroeten,\nSense Lifting`
              :`Hi,\n\n${myName} wants to add you as a friend on Sense Lifting.\n\nClick here to accept or decline:\n${inviteUrl}\n\nBest,\nSense Lifting`;
            return(
              <div key={r.to} style={s_GC({padding:14,marginBottom:8})}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  {u
                    ? <Avatar user={u} size={44} profileImage={u.picture||null} onClick={()=>onOpenProfile(u)}/>
                    : <div style={{width:44,height:44,borderRadius:"50%",background:`${_T.accent}20`,border:`1px solid ${_T.accent}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:_T.accent}}>?</div>
                  }
                  <div style={{flex:1}}>
                    <div style={{color:C.white,fontSize:14,fontWeight:600}}>{name}</div>
                    <div style={{fontSize:11,color:C.silver,marginTop:2}}>{nl?"Verzoek verzonden":"Request sent"}</div>
                  </div>
                  <div style={{background:`${_T.accent}18`,border:`1px solid ${_T.accent}44`,borderRadius:20,padding:"4px 12px"}}>
                    <span style={{fontSize:11,color:_T.accent}}>⏳ {nl?"In afwachting":"Pending"}</span>
                  </div>
                </div>
                {inviteUrl && (
                  <div style={{display:"flex",gap:8,marginTop:10,paddingTop:10,borderTop:`1px solid ${_T.cardBorderBot}`}}>
                    <a href={`https://wa.me/?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noopener noreferrer"
                      style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:"#25D366",border:"none",borderRadius:10,padding:"8px 12px",color:"#fff",fontSize:12,fontWeight:600,textDecoration:"none",cursor:"pointer"}}>
                      💬 WhatsApp
                    </a>
                    <a href={`mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`}
                      style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,borderRadius:10,padding:"8px 12px",color:C.silver2,fontSize:12,fontWeight:600,textDecoration:"none",cursor:"pointer"}}>
                      ✉️ E-mail
                    </a>
                  </div>
                )}
              </div>
            );
          })}

          {incoming.length===0&&outgoing.length===0&&(
            <div style={{...s_GC({textAlign:"center",padding:28}),color:C.silver,fontSize:13}}>{nl?"Geen verzoeken.":"No pending requests."}</div>
          )}
        </div>
      )}

      {/* ── Discover tab ── */}
      {subtab==="discover"&&(
        <div>
          <input value={discoverSearch} onChange={e=>setDiscoverSearch(e.target.value)} placeholder={nl?"Zoek op naam...":"Search by name..."} style={s_TI({marginBottom:10,padding:"9px 12px",fontSize:13})}/>
          {otherUsers.filter(u=>!discoverSearch||u.name.toLowerCase().includes(discoverSearch.toLowerCase())).length===0&&(
            <div style={{...s_GC({textAlign:"center",padding:28}),color:C.silver,fontSize:13}}>
              {discoverSearch?(nl?"Geen gebruikers gevonden.":"No users found."):(nl?"Iedereen is al je vriend!":"Everyone is already your friend!")}
            </div>
          )}
          {otherUsers.filter(u=>!discoverSearch||u.name.toLowerCase().includes(discoverSearch.toLowerCase())).map(u=>(
            <div key={u.id} style={s_GC({padding:14,marginBottom:8,display:"flex",alignItems:"center",gap:12})}>
              <Avatar user={u} size={44} profileImage={u.picture||null} onClick={()=>onOpenProfile(u)}/>
              <div style={{flex:1,cursor:"pointer"}} onClick={()=>onOpenProfile(u)}>
                <div style={{color:C.white,fontSize:14,fontWeight:600}}>{u.name}</div>
                {u.intention&&<div style={{color:C.silver,fontSize:11,marginTop:2,fontStyle:"italic"}}>"{u.intention.slice(0,40)}{u.intention.length>40?"...":""}"</div>}
              </div>
              <button onClick={()=>onFriendRequest(u.id)} style={s_PB({padding:"7px 14px",fontSize:12,borderRadius:9})}>+</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default FriendsScreen;
